import type { DraftKind } from './types';
import { formatDeOneDecimal } from './utils/numberFormatting';
import type { FaceFn } from './services/categoryMeta';

export const POLLING_INTERVAL_MS = 10_000;
export const HEALTH_POLL_MS = 30_000;
export const UPLOAD_THROTTLE_MS = 1_500;

// ─── Category & Reason Code Constants ─────────────────
export const CATEGORY_KEYS = ['K0', 'K1', 'K2', 'K3A', 'K3B', 'K4', 'KU'] as const;
// Why: KU (the unclassified bucket) is display-only — forcing to KU is nonsensical
export const ASSIGNABLE_CATEGORIES = ['K0', 'K1', 'K2', 'K3A', 'K3B', 'K4'] as const;

export const CATEGORY_LABELS: Record<string, string> = {
  K0: 'Standard-Prozess',
  K1: 'Standard-Info',
  K2: 'Technische Anfrage',
  K3A: 'Beschwerde',
  K3B: 'Eskalation',
  K4: 'Gefahr im Verzug',
  // WIM triage role: primaryAxis = handling id, secondaryAxis[0] = process_type id.
  // NO surface reads this map directly any more — the former triage panel's carve-out (a direct
  // `CATEGORY_LABELS[h] ?? h`) was removed once a tenant could re-word its own vocabulary, because
  // it made the panel the only place a rename could NOT reach; panel and row would have disagreed
  // on one screen. Every consumer now goes through categoryMeta.resolveLabel(), which reaches this
  // tier ONLY for a role that OWNS the legacy K-vocabulary (gMSB/wMSB). A wim ticket with no
  // vocabulary loaded therefore gets the placeholder floor rather than borrowing these words —
  // see categoryMeta.ts's role gate. These wim entries survive as the backstop the wim vocabulary
  // overrides, and as themeContrast's coherence-pin counterpart, not as a rendering path.
  // Ids are collision-free across roles (K-codes vs slugs).
  //
  // RE-KEYED at vocabulary v7 (wim vocabulary v7, 2026-09-13): the handling axis moved from four
  // opaque letter-plus-digit codes to five semantic slugs (auskunft is NEW — the lookup-and-tell
  // outcome that used to be filed as an internal reply), and the process-type axis was re-derived
  // from the client-inbox forensics into the 18 below. There is deliberately NO code→slug mapping
  // anywhere: the demo tenant is purged and re-ingested, so a code-keyed row is not a state that
  // exists — and the retired codes are not spelled anywhere in this bundle, by design.
  // Backstop words carry the canonical German spelling even where the seed still serves an ASCII
  // transliteration — the served row wins at render time, and mirroring the transliteration here
  // would bake a known seed defect into the bundle.
  weiterleiten: 'Weiterleiten',
  antworten: 'Antworten',
  auskunft: 'Auskunft',
  zur_kenntnis: 'Zur Kenntnis',
  pruefen: 'Prüfen',
  anmeldung: 'Anmeldung',
  abmeldung: 'Abmeldung',
  geraeteuebernahme: 'Geräteübernahme',
  zaehlertausch: 'Zählertausch',
  stammdaten: 'Stammdaten / SDÄ',
  stammdaten_anforderung: 'Stammdaten-Anforderung',
  messwerte: 'Messwerte',
  lastgang_bilanzierung: 'Lastgang / Bilanzierung',
  marktnachricht_fehler: 'Marktnachricht-Fehler',
  quotes: 'QUOTES',
  einspeisung_eeg: 'Einspeisung / EEG',
  messkonzept_wandler: 'Messkonzept / Wandler',
  rechnungsausgang: 'Rechnungsausgang',
  lf_sd_zuordnung: 'LF/SD-Zuordnung',
  sondergebiete: 'Sondergebiete',
  beschwerde: 'Beschwerde',
  zustaendigkeitsklaerung: 'Zuständigkeitsklärung',
  sonstiges: 'Sonstiges',
};

// WIM handling ids by MEANING — the single FE enumeration of the handling axis. Comparisons key
// here (forward-eligibility etc.), never to a raw slug at the call site; theme.wimHandling and
// CATEGORY_LABELS' handling entries are keyed against this set by the coherence pin in
// themeContrast. The KEYS are the meanings this frontend reasons about and the VALUES are core's
// HandlingIds slugs — the split is what let the ids stop being opaque codes without a single call
// site changing: `lookup` joined as a fifth meaning, `wimIntern` became `reply` (the word for what
// it does, now that the id no longer needs a code to explain it).
// part 7, 2026-10-13 (operator ruling): the filing handling was renamed Ablegen -> Zur Kenntnis as
// a CONCEPT (slug `zur_kenntnis`, abbr ZK). Its key here is `noted`, not `archive`: `archive` is
// also the name of a DRAFT KIND (the no-letter claim), and one word for two things was the
// confusion the rename removes.
export const WIM_HANDLING = {
  forward: 'weiterleiten',
  reply: 'antworten',
  lookup: 'auskunft',
  noted: 'zur_kenntnis',
  review: 'pruefen',
} as const;
export type WimHandlingId = typeof WIM_HANDLING[keyof typeof WIM_HANDLING];
export const WIM_HANDLING_IDS: readonly WimHandlingId[] = Object.values(WIM_HANDLING);

// The code constant `CLAIM_KIND_HANDLING` that used to live here is served now, as
// `useCategoryMeta().decisionKindHandling` (part 4c, 2026-09-27); `WIM_HANDLING` above stays as the
// handling-id enum.

// The receipt is a COMPANION ARTIFACT, never a decision (R31, 2026-09-18) — the one claimed kind
// that produces no decision badge and no decision date. It lives HERE, rather than at either
// surface that asks: it is a RULING about what a claim MEANS, and the row (components/shared.tsx)
// and the detail's claim-timestamp resolver (services/draftClaims.ts) must not each carry their own
// `kind !== 'ack'` — a reader who finds one should find the reason with it, and two spellings of a
// ruling is how the two surfaces start disagreeing about which claims count.
export const isDecisionKind = (kind: DraftKind): boolean => kind !== 'ack';

// FE mirror of core MsbKCatalog.UnclassifiedId: the one key the categorization-performance
// views exclude (KU rides total-reconciliation views only).
export const UNCLASSIFIED_KEY = 'KU';

// Why: EDIFACT signal atoms only exist for K0/K1/K2 (tools/signals/nb/edifact/).
// Without this gate the BE 400s on K3A/K3B/K4 + EDIFACT with "no atoms found".
export const EDIFACT_CATEGORIES = ['K0', 'K1', 'K2'];
export const EDIFACT_FALLBACK_CATEGORY = 'K2';
export const TESTGEN_DEFAULT_TARGET_CATEGORY = 'K2';

export const REASON_CODES = [
  { code: 'termin_status', label: 'Reine Termin-/Statusanfrage', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'standard_prozess', label: 'Standard-Prozess (PIN/Baustrom/Ablesung/HW-Defekt)', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'kontaktformular', label: 'Kontaktformular / Allgemeine Anfrage', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'nicht_zustaendig', label: 'Nicht zuständig / Falsche Adresse', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'irrlaeufer_intern', label: 'Irrläufer (intern)', minCategory: 'K0', maxCategory: 'K0' },
  { code: 'allgemeine_info', label: 'Allgemeine Information (Preise/Datenschutz/iMSys)', minCategory: 'K1', maxCategory: 'K1' },
  { code: 'technische_bewertung', label: 'Technische Bewertung nötig (TAB/RFZ/Klassenzuordnung)', minCategory: 'K2', maxCategory: 'K2' },
  { code: 'systemrecherche', label: 'Systemrecherche / Zählernummer / MaLo-ID', minCategory: 'K2', maxCategory: 'K2' },
  { code: 'koordination_drittpartei', label: 'Koordination mit Installateur / Drittpartei', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'leistungsaenderung', label: 'Leistungsänderung / Wallbox / PV / Einspeisung', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'eigentuemerwechsel', label: 'Eigentümerwechsel / Umzug / Todesfall', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'datenschutz', label: 'Datenschutz / DSGVO / Datenzugriff', minCategory: 'K1', maxCategory: 'K2' },
  { code: 'steuerung_14a', label: 'Steuerung §14a EnWG', minCategory: 'K1', maxCategory: 'K2' },
  { code: 'anhang_verwiesen', label: 'Anhang verwiesen (Inhalt nicht sichtbar)', minCategory: 'K2', maxCategory: 'K2' },
  { code: 'msb_wechsel', label: 'MSB-Wechsel (§5 MsbG)', minCategory: 'K1', maxCategory: 'K3A' },
  { code: 'messstreitigkeit', label: 'Messstreitigkeit / Verbrauchsabweichung', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'abrechnungsstreit', label: 'Abrechnungsstreit / Entgeltforderung', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'rueckrufbitte', label: 'Rückrufbitte', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'vollmacht_vertretung', label: 'Vollmacht / Vertretung / Hausverwaltung', minCategory: 'K0', maxCategory: 'K2' },
  { code: 'mehrere_anliegen', label: 'Mehrere Anliegen gemischt', minCategory: 'K2', maxCategory: 'K3A' },
  { code: 'beschwerde', label: 'Beschwerde / Frust (ohne rechtliche Drohung)', minCategory: 'K3A', maxCategory: 'K3A' },
  { code: 'eskalation', label: 'Eskalation: Drohung / Anwalt / Frist / GF-Bezug', minCategory: 'K3B', maxCategory: 'K3B' },
  { code: 'akute_gefahr', label: 'Akute Gefahr (Gas/Brand/Spannung)', minCategory: 'K4', maxCategory: 'K4' },
  { code: 'sonstiges', label: 'Sonstiges', minCategory: undefined, maxCategory: undefined },
  { code: 'unbestimmt', label: 'Nicht bestimmbar', minCategory: undefined, maxCategory: undefined },
] as const;

type ReasonCodeEntry = typeof REASON_CODES[number];
const REASON_CODE_MAP = new Map<string, ReasonCodeEntry>(REASON_CODES.map(rc => [rc.code, rc]));

export const getReasonCodeLabel = (code: string): string =>
  REASON_CODE_MAP.get(code)?.label ?? code;

/** Display text for a secondary-axis pill: de-slugged and lowercased.
 *  Lowercasing is EXPLICIT rather than inherited from the corpus — every id ships lowercase today,
 *  so relying on that would make the rule and the coincidence indistinguishable, and a future
 *  mixed-case id would silently break the ruling with no test to catch it. */
export const formatSecondaryAxisCode = (code: string): string => code.replace(/_/g, ' ').toLowerCase();

/** How many secondary-axis pills a surface shows before „+n" (SecondaryAxisPills): a number is a
 *  fixed cap; 'fit' shows as many as the line has room for (the list row and the Archiv row, small
 *  facts on a row 2026-10-19); 'all' never collapses (the detail header). */
export const SECONDARY_AXIS_MAX_VISIBLE = {
  dashboard: 3,
  uploads: 5,
  responses: 'fit',
  detail: 'all',
  chatbot: 3,
} as const;

// ─── OCR Options ─────────────────────────────────────
export const OCR_OPTIONS = [
  { value: 'auto', label: 'KI' },
  { value: 'force', label: 'OCR' },
  { value: 'skip', label: 'Skip' },
] as const;

// Shared with categoryMeta.ts's resolveCategoriesForSecondaryAxisCode (the served-aware seam entry point,
// D10 addendum 4) — both expand a min/max PAIR the same way, whether it came from a baked
// REASON_CODES row or a served grund_code value's own attributes. ASSIGNABLE_CATEGORIES (not a
// re-declared literal) is the one home for "the ordered K-band a range can slice" — see D9 for the
// KU exclusion reasoning that already made it the right set for this.
export const expandCategoryRange = (min: string, max: string): string[] => {
  const minIdx = ASSIGNABLE_CATEGORIES.indexOf(min as typeof ASSIGNABLE_CATEGORIES[number]);
  const maxIdx = ASSIGNABLE_CATEGORIES.indexOf(max as typeof ASSIGNABLE_CATEGORIES[number]);
  if (minIdx < 0 || maxIdx < 0) return [];
  return ASSIGNABLE_CATEGORIES.slice(minIdx, maxIdx + 1) as string[];
};

// THE FLOOR TIER for grund-code -> category-band mapping. categoryMeta.ts's
// resolveCategoriesForSecondaryAxisCode is the served-aware entry point most call sites should use — this
// stays the pure baked lookup it consults when a vocabulary serves no row for the code (or serves
// none at all, the pre-vocabulary-delivery state every MSB tenant is in today).
export const getCategoriesForReasonCode = (code: string): string[] => {
  const rc = REASON_CODE_MAP.get(code);
  if (!rc?.minCategory || !rc?.maxCategory) return [];
  return expandCategoryRange(rc.minCategory, rc.maxCategory);
};

// ─── Feature Flags ───────────────────────────────────
export const SHOW_EINGEREICHT_IN_AS = false;

// ─── Status Group Constants ───────────────────────────
export const STATUS_GROUPS = {
  eingereicht: ['submitted', 'ingested'] as string[],
} as const;
// The settled statuses (the Entschieden tab) live in services/settledRow.ts (SETTLED_STATUSES) —
// one home, read by every gate that asks whether a row is decided.

// ─── AI descriptors ─────────────────────────────────────────────────────────────────────────
// The KEYS of the AI descriptor split pills — pipeline nouns, the same word on every surface and
// tenant (detail, Statistik, export, PDF; core carries the same strings). They name the pipeline
// stage, not the role's artefact, so no role can conflate them: a Kategorie, a Marktprozess and a
// Bearbeitung all have a Klassifikationskonfidenz. The older role-flavoured names (Kategoriekonfidenz,
// Antwortsicherheit) are retired on every tenant. Neither is an empirical rate: the approval rate is
// the Abnahmequote, a separate metric.
export const AI_DESCRIPTOR = {
  /** The classifier's self-reported confidence in its primary-axis answer. */
  classification: 'Klassifikationskonfidenz',
  /** The composer's self-reported confidence in the letter it wrote. */
  suggestion: 'Vorschlagskonfidenz',
  /** created_at → the suggestion's completion, as the row's processing_time_ms. */
  responseTime: 'KI-Antwortzeit',
  /** The key of a row no composer wrote a letter for; its value is NO_DRAFT_FACE. */
  noDraft: 'Kein Entwurf',
} as const;
/** The value segment of the no-draft descriptor: the clerk answers by hand. */
export const NO_DRAFT_FACE = 'Von Hand';

/** A percentage as the descriptor pills print it: „86 %" — whole number, a space before the sign. */
export const formatPercentFace = (fraction: number): string => `${Math.round(fraction * 100)} %`;
/** A duration in milliseconds as the descriptor pills print it: „10,7 s" — de-DE, one decimal. */
export const formatSecondsFace = (ms: number): string => `${formatDeOneDecimal(ms / 1000)} s`;

// ─── Kopieren confirmations (Part C, 2026-09-07) ─────────────────
// A copy records an indirect approval, so it is stated and it is takeable-back. Both hosts that can
// show the toast — AntwortenSeite and the pop-out window — say the SAME thing for the same act and
// leave the SAME window to undo it; the wording and the duration therefore live here once rather
// than as four literals two files apart.
export const COPY_TOAST_MESSAGE = {
  /** The letter flow: one suggestion, copied whole. */
  suggestion: 'Vorschlag kopiert — zählt als genehmigt',
  /** One triage draft of a row that may carry two. */
  draft: 'Entwurf kopiert — zählt als genehmigt',
} as const;
// The RECEIPT's own toast (R31/R32, 2026-09-18) is built in copyToastMessage below from the SERVED
// `draft_kind.ack` word (part 4c, 2026-09-27) — the code-owned `receipt` variant this object used
// to carry is deleted outright (no-deprecation-ceremonies). It is still the one copy toast that
// does NOT say „zählt als genehmigt", because it does not: an Empfangsbestätigung tells the sender
// their mail arrived; it settles nothing about how the row will be handled, records no
// copy-approval server-side, and counts toward no approval rate.

/**
 * THE copy toast for a gesture, so the two hosts that show one (AntwortenSeite and PopoutShell)
 * cannot pick differently. `kind` is the triage draft that was copied, or undefined for the letter
 * flow's single suggestion. `face` is the tenant's served vocabulary (`useCategoryMeta().face`) —
 * the receipt toast names the SERVED word, so a tenant that re-words `draft_kind.ack` sees its own
 * word in the toast without a line here changing.
 */
export function copyToastMessage(kind: DraftKind | null | undefined, face: FaceFn): string {
  if (!kind) return COPY_TOAST_MESSAGE.suggestion;
  return kind === 'ack' ? `${face('draft_kind', 'ack') ?? 'ack'} kopiert` : COPY_TOAST_MESSAGE.draft;
}
// Longer than an info toast, shorter than a bulk undo: a copy is a single deliberate act the clerk
// is still looking at, and the Rückgängig has to outlive the paste it was made for.
export const COPY_TOAST_MS = 8000;

// ─── Personal Data Field Labels ─────────────────────

type ApiFieldKey =
  | 'emails'
  | 'phoneNumbers'
  | 'ibans'
  | 'dates'
  | 'postalCodes'
  | 'names';

// Keys match API JSON field casing (camelCase).
export const PERSONAL_DATA_LABELS: Record<ApiFieldKey, string> = {
  emails: 'E-Mail-Adressen',
  phoneNumbers: 'Telefonnummern',
  ibans: 'IBANs',
  dates: 'Datumsangaben',
  postalCodes: 'Postleitzahlen',
  names: 'Namen',
};

export const normalizeKey = (key: string): ApiFieldKey =>
  (key[0].toLowerCase() + key.slice(1)) as ApiFieldKey;

// ─── Status Labels (German UI) ──────────────────────
// 'approved' deliberately has NO entry here (fix wave 2026-09-27, item 2): an approved ticket's
// status word IS the served `decision.approve` face („Genehmigt" on the seed) — the same word the
// Statistik card and the export wear for the approved count — not a second, code-owned literal a
// re-worded tenant would leave stale. `statusLabel` below is the one place that seam is composed;
// every other status here names no served id and stays a plain floor.
export const STATUS_LABELS: Record<string, string> = {
  // The „Nicht hilfreich" verdict (core 2026-10-16): an ACTIVE status since "Genehmigen settles the
  // row" (2026-10-23) — a declined row sits in Entschieden beside the approved ones until filed.
  declined: 'Nicht hilfreich',
  submitted: 'Eingereicht',
  ingested: 'Aufgenommen',
  categorizing: 'Kategorisierung',
  categorized: 'Kategorisiert',
  generating_suggestion: 'Vorschlag wird erstellt',
  suggestion_ready: 'Vorschlag bereit',
  sent: 'Gesendet',
  // Names no served id — no decision/bucket/outcome/draft_kind family serves a "reject" word, and
  // this status predates the current triage model (ruled chrome, fix wave 2026-09-27, item 2; see
  // badgeFactLiterals.test.ts's CHROME_EXCEPTIONS for the same ruling).
  rejected: 'Abgelehnt',
  reanalyzing: 'Erneut analysiert',
  pipeline_error: 'Fehler',
  archived: 'Archiviert',
} as const;

/** A ticket's pipeline status, in German. `approved` is the one status that IS a served id (see
 *  the comment on STATUS_LABELS above) — read through the `decision.approve` face the Statistik card
 *  and the approved-as badge already wear, so a re-worded decision word reaches this label too.
 *  (Since "Genehmigen settles the row" the TAB is Entschieden and holds the no as well, so the tab's
 *  word is no longer the approved status's word.) Every other status floors to STATUS_LABELS, then
 *  to the raw key — the same floor this table has always had, status by status.
 *  `configLoading` is a SEPARATE signal from `face`'s own return (fix loop pass 1, item 3):
 *  resolveFace's raw-key floor answers the literal key for an unresolved vocabulary (its
 *  absent-family branch, not its served-null one), so `face(...) ?? status` alone painted a raw key
 *  on the clerk's Status field for the whole loading window. While loading this floors to an empty
 *  value instead (both call sites render a plain string, not JSX, so a SkeletonBlock cannot stand
 *  in here). Once config settles, an explicit served `null` keeps the raw-status-key fallback. */
export function statusLabel(status: string | null | undefined, face: FaceFn, configLoading: boolean): string {
  if (!status) return '';
  if (status === 'approved') return configLoading ? '' : (face('decision', 'approve') ?? status);
  return STATUS_LABELS[status] ?? status;
}

export const PIPELINE_STAGE_LABELS: Record<string, string> = {
  parse: 'Parsen',
  pii_extraction: 'Personendaten-Erkennung',
  attachment_extraction: 'Anhang-Extraktion',
  categorization: 'Kategorisierung',
  ocr_execution: 'OCR',
  suggestion_generation: 'Antwortgenerierung',
  draft_generation: 'Entwurfserstellung',
};

// THE „Formular" word (part 7 fix loop, 2026-10-12) — ONE constant for the row pill's face
// (FormPill), its „Hinweise in der Liste" switch (services/rowPillKinds) and its filter bubble
// (FilterPanel), so the three can never word the one fact two ways. A PRODUCT word, not a served
// tenant word: form extraction is a capability (/config `formExtractionEnabled`), not a vocabulary
// value, so no tenant serves a face for it.
export const FORM_WORD = 'Formular';

// THE „Frist genannt" word — one constant for the list row's pill (FristGenanntPill) and its
// „Hinweise in der Liste" switch (services/rowPillKinds). A product word on every role: the
// letter check finds a deadline in the mail, no tenant vocabulary serves it.
export const FRIST_GENANNT_WORD = 'Frist genannt';
